import { Persistence } from "../../config/constants";
import { TickLayerName } from "../../config/tickLayers";
import { BaseEntity } from "../../core/entity/BaseEntity";
import { Entity } from "../../core/entity/Entity";
import { on } from "../../core/entity/handler";
import { cellDust, dustSlowdown } from "../../game/Dust";
import { CleaningWorld, sweepPass } from "../../game/game-actions/dust-actions";
import {
  shopVacPass,
  vacuumPass,
} from "../../game/game-actions/shop-vac-actions";
import { heldTool } from "../../game/HeldTool";
import { personCanWork } from "../../game/Person";
import { SWEEPING_PENALTY } from "../../game/player-motion";
import { SHOP_VAC_DRAG_PENALTY } from "../../game/ShopVac";
import { cleaningGear } from "../commands/cleaning-commands";
import { Player } from "../entities/Player";
import { ShopVacEntity } from "../entities/ShopVacEntity";
import { machineStatesNow } from "../machine-reads";
import { projectPerson, projectProgression } from "../projection";
import { Broom } from "../singletons/Broom";
import { DustLayer } from "../singletons/DustLayer";
import { Progression } from "../singletons/Progression";
import { ShopInfo } from "../singletons/ShopInfo";
import { TimeFlow } from "../TimeFlow";

/**
 * The cleaning slice of the engine tick: the broom's sweep, the vac's
 * suction, and the vac's passive trickle, in that order (sweep → vacuum
 * → shopVac), every engine tick on the "cleaning" layer for the shop
 * minutes TimeFlow brought — before machines emit this tick's dust, so
 * cleaning always clears the floor as of the last one.
 *
 * Unlike the MachineSystem (whose state lives spread across per-machine
 * entities), the cleaning passes read and write a handful of singleton
 * slices — the dust map, the dustpan, the vac's canister, sweeping XP —
 * so the pass reuses the pure passes wholesale: build a projection
 * snapshot, run `sweepPass` / `vacuumPass` / `shopVacPass` over it for
 * the span, and write the slices they own back onto their entities. The
 * passes emit no sounds and never freeze the feet (walking and sweeping
 * happen together), so nothing else writes back.
 *
 * The system also carries the cleaning half of the time model and the
 * walk-speed penalties:
 *  - a held tool worked by the operate key spends the player's time,
 *    registered with TimeFlow as a spender — the busy-body spender is
 *    the Player's own;
 *  - deep sawdust underfoot, a dragged vac, and an active sweep slow the
 *    walk (`Player.walkSpeed`), registered as speed penalties on the
 *    Player. A load rebuilds the Player entity, so the penalties are
 *    re-registered from `beforeTick` (which runs before the player
 *    layer's movement) whenever the instance changes.
 *
 * Transient — never serialized; added per session by the bootstrap.
 */
export class CleaningSystem extends BaseEntity implements Entity {
  id = "cleaningSystem";
  tickLayer: TickLayerName = "cleaning";
  persistenceLevel: number = Persistence.Permanent;

  private unregisterSpender: (() => void) | null = null;
  /** The Player instance the speed penalties are registered on. */
  private penalizedPlayer: Player | null = null;

  @on("afterAdded")
  onAfterAdded() {
    const timeFlow = this.game.entities.tryGetSingleton(TimeFlow);
    if (timeFlow) {
      this.unregisterSpender = timeFlow.registerSpender(() =>
        this.heldToolSpendsTime(),
      );
    }
  }

  @on("destroy")
  onDestroy() {
    this.unregisterSpender?.();
    this.unregisterSpender = null;
  }

  @on("beforeTick")
  onBeforeTick() {
    this.ensureSpeedPenalties();
  }

  @on("tick")
  onTick() {
    const timeFlow = this.game.entities.tryGetSingleton(TimeFlow);
    const minutes = timeFlow?.gameDt ?? 0;
    if (minutes > 0) {
      this.pass(minutes);
    }
  }

  /**
   * Whether a held cleaning tool is consuming the player's time: the
   * operate key held with a tool in hand and the player free to work.
   */
  heldToolSpendsTime(): boolean {
    const player = this.game.entities.getSingleton(Player);
    return (
      player.operating === true &&
      heldTool(cleaningGear(this.game)) !== null &&
      personCanWork(player)
    );
  }

  /** The passes' world, assembled from the live singletons. */
  private world(): CleaningWorld {
    const game = this.game;
    const broom = game.entities.tryGetSingleton(Broom);
    const vac = game.entities.tryGetSingleton(ShopVacEntity);
    return {
      player: projectPerson(game),
      machines: machineStatesNow(game),
      shopInfo: game.entities.getSingleton(ShopInfo).info,
      dust: game.entities.tryGetSingleton(DustLayer)?.map ?? {},
      dustpan: broom?.dustpan ?? {},
      broomOwned: broom?.owned ?? false,
      broomPosition: broom?.position ?? null,
      shopVac: vac?.view() ?? null,
      progression: projectProgression(game),
    };
  }

  /** `minutes` of cleaning: the three passes, written back. */
  private pass(minutes: number): void {
    const before = this.world();
    const after = shopVacPass(minutes)(
      vacuumPass(minutes)(sweepPass(minutes)(before)),
    );
    if (after === before) {
      return;
    }

    if (after.dust !== before.dust) {
      const dustLayer = this.game.entities.tryGetSingleton(DustLayer);
      if (dustLayer) {
        dustLayer.map = after.dust;
      }
    }
    if (after.dustpan !== before.dustpan) {
      const broom = this.game.entities.tryGetSingleton(Broom);
      if (broom) {
        broom.dustpan = after.dustpan;
        this.game.dispatch("cleaningChanged", {});
      }
    }
    if (after.shopVac !== before.shopVac && after.shopVac) {
      const vac = this.game.entities.tryGetSingleton(ShopVacEntity);
      if (vac) {
        vac.position = after.shopVac.position;
        vac.canister = after.shopVac.canister;
        this.game.dispatch("cleaningChanged", {});
      }
    }
    if (after.progression !== before.progression) {
      // The passes grant XP through the shared withXp, which also banks
      // the level-ups; copy both results back.
      const progression = this.game.entities.getSingleton(Progression);
      progression.xp = after.progression.xp;
      progression.skillPoints = after.progression.skillPoints;
      this.game.dispatch("progressionChanged", {});
    }
  }

  /**
   * Register the walk-speed penalties on the current Player instance —
   * once per instance; a save load rebuilds the Player and its old
   * registrations die with it.
   */
  private ensureSpeedPenalties(): void {
    const player = this.game.entities.tryGetSingleton(Player);
    if (!player || player === this.penalizedPlayer) {
      return;
    }
    // Deep sawdust underfoot: the same ramp that slows a buried machine.
    player.registerSpeedPenalty(() => {
      const dustLayer = this.game.entities.tryGetSingleton(DustLayer);
      return dustLayer ? dustSlowdown(cellDust(dustLayer.map, player.cell)) : 0;
    });
    // Dragging the vac's canister adds a point of walk penalty.
    player.registerSpeedPenalty(() => {
      const vac = this.game.entities.tryGetSingleton(ShopVacEntity);
      return vac?.inHand ? SHOP_VAC_DRAG_PENALTY : 0;
    });
    // Actively plowing dust has heft — you walk, but not at full stride.
    player.registerSpeedPenalty(() => {
      const broom = this.game.entities.tryGetSingleton(Broom);
      return broom?.inHand && player.operating ? SWEEPING_PENALTY : 0;
    });
    this.penalizedPlayer = player;
  }
}
